import React from "react";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { MedicalHistoryCard } from "components/PatientDetailCardWithOverview/components/PatientOverviewModal/components/MedicalHistory/MedicalHistoryCard.jsx";
import { useUserRole } from "components/Sidebar/hooks/useUserRole";
import { PatientDetailCard } from "features/patients/components/PatientDetailCard";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePracticeSettings } from "features/practice/hooks/usePracticeSettings";
import { TENANT_TYPES } from "utils/tenantAccess";

import { AlergiesIntolerance as AllergiesIntolerance } from "../PatientDetailsPage/components/AllergiesIntolerance/AllergiesIntolerance";
import { AcuteMedication } from "./components/ActuteMedication/AcuteMedication.jsx";
import { LongTermMedication } from "./components/LongTermMedication/LongTermMedication.jsx";
import { Prescriptions } from "./components/Prescriptions/Prescriptions.jsx";
import s from "./PatientMedicationPage.module.css";

const PatientMedicationPage = () => {
  const queryClient = useQueryClient();
  const patientId = useCurrentPatientId();
  const { tenantType } = useUserRole();
  const { data: practiceSettings } = usePracticeSettings();

  // Prescriptions is shown to every practice by default and is only hidden for
  // a CONSULT practice that has explicitly turned it off in Practice Settings
  // (practice_settings.prescriptions, see z_219) — GP practices always see it.
  const hidePrescriptions =
    tenantType?.toUpperCase() === TENANT_TYPES.CONSULT &&
    practiceSettings?.prescriptions === false;

  const tenantVal = localStorage.getItem("kinesin-tenant");

  const deleteData = async (id, stopReason) => {
    try {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.post(`${tenantVal}/patientmedication/stop/` + id, {
        stopReason,
      });
      toast.success("Medication has been stopped");
      queryClient.invalidateQueries({ queryKey: ["longTermMedications"] });
      queryClient.invalidateQueries({ queryKey: ["acuteMedications"] });
      queryClient.invalidateQueries({
        queryKey: ["paginatedAcuteMedications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["paginatedLongTermMedications"],
      });
    } catch (error) {
      toast.error(`Error fetching data: ${error.message}`);
      console.error("There was a problem with the axios operation:", error);
    }
  };

  return (
    <div style={{ margin: "0 auto" }}>
      <div className={s.wrapper}>
        <div className={s.firstColumnWrapper}>
          <>
            <PatientDetailCard />
            <AllergiesIntolerance />
            <MedicalHistoryCard
              dateRecorderColumnWidth="minmax(130px, 1fr)"
            />
          </>
        </div>
        <div className={s.secondColumnWrapper}>
          {!hidePrescriptions && <Prescriptions patientId={patientId} />}
          <LongTermMedication patientId={patientId} deleteDrug={deleteData} />
          <AcuteMedication patientId={patientId} deleteDrug={deleteData} />
        </div>
      </div>
    </div>
  );
};

export { PatientMedicationPage };
